import React, { useCallback, useState } from 'react';

type Props = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & {
  onClickAsync: () => Promise<any> | void;
  busyText?: string;
  startDelayMs?: number;
};

/**
 * Button that manages its own busy state.
 * - Disables itself while running
 * - Shows a spinner
 */
export function AsyncButton({
  onClickAsync,
  busyText,
  startDelayMs = 180,
  disabled,
  children,
  ...rest
}: Props) {
  const [busy, setBusy] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);

  const onClick = useCallback(async () => {
    if (busy || disabled) return;
    setBusy(true);
    setShowSpinner(false);
    const t = window.setTimeout(() => setShowSpinner(true), startDelayMs);
    try {
      await onClickAsync();
    } finally {
      window.clearTimeout(t);
      setBusy(false);
      setShowSpinner(false);
    }
  }, [busy, disabled, onClickAsync, startDelayMs]);

  return (
    <button
      {...rest}
      disabled={disabled || busy}
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      {showSpinner ? <span className="spinner" aria-hidden="true" /> : null}
      <span>{busy ? (busyText || children) : children}</span>
    </button>
  );
}
